import React, { forwardRef, useCallback, useRef, useState, useImperativeHandle } from "react";
import { RCCGrid, RCCHookToast, RCCRefModalProps, RCCTextArea, RCCRangeSlider, RCCAlert, RCCServicesApi, RCCProgress } from "react-common-components";
import { useForm } from "react-hook-form";
import DateTimePicker from "src/components/DateTimePicker";
import InputNumber from "src/components/InputNumber";
import Modal from "src/components/Modal";
import TimelineVertical from "src/components/TimelineVertical";
import Title from "src/components/Title";
import { useLoaderBackdrop } from "src/context/LoaderBackdrop/context";
import { useTranslation } from "src/context/Translations/context";
import { getValidationErrors, standardErrorMessages } from "src/utils/errors";
import formatNumber from "src/utils/formatNumber";
import stringToNumber from "src/utils/stringToNumber";
import { MeasurementForm, MeasurementType } from "../keyResults/interface";
import IOrgChartCheckInRef from "./interface";
import  { checkInMilestoneSchema, checkInSchema } from "./schemas";
import styledModalCheckIn, { DivCheckInModal, styledModalCheckInMilestone } from "./styles";


const CheckInModal: React.ForwardRefRenderFunction<IOrgChartCheckInRef> = (_, ref) => {
	const { currentLanguage, translate } = useTranslation();
	const { setLoading } = useLoaderBackdrop();
	const {addToast} = RCCHookToast();

	// Modal Add/Change Key Results
	const onCallbackAddOrChange = useRef<Function | undefined>(undefined);
	const [modalSubtitle, setModalSubtitle] = useState("");
	const [selectedKr, setSelectedKr] = useState<any>({});

	// Modal Check In
	const modalPerformCheckIn = useRef<RCCRefModalProps>(null);
	const [showTimeLine, setShowTimeLine] = useState(false);
	const [percentWhenTypeNotKR, setPercentWhenTypeNotKR] = useState<number>(0);
	const { 
		handleSubmit : handleSubmitPerformCheckIn, register : registerPerformCheckIn, control: controlPerformCheckIn ,
		reset : resetPerformCheckIn, setError : setErrorPerformCheckIn, formState: { errors : errorsPerformCheckIn }, clearErrors: clearErrorsPerformCheckIn
	} = useForm();
	const openModalPerformCheckIn = useCallback(async (
		krId: number, 
		krTitle: string, 
		measurementType: string,
		measurementForm: string, 
		realizedValue: string | number, 
		percent: string | number,
		colorGraph: string, 
		initialValue?: string | number, 
		goal?: string | number, 
		onCallback?: Function
	) => {
		resetPerformCheckIn();
		onCallbackAddOrChange.current = onCallback;
		setModalSubtitle(krTitle);
		setShowTimeLine(measurementType.toString() === MeasurementType.MILESTONE);

		if(measurementType.toString() === MeasurementType.MILESTONE){
			try{
				setLoading(true);
				const {data} = await RCCServicesApi.getRequest({
					method: "KR/SelectMilestones",
					urlParameters: {
						krId
					}
				});
				setSelectedKr({
					okb001Id: krId,
					okb001Tipomedicao: measurementType,
					okb001Formamedicao: measurementForm,
					oktbb003: data,
					oktbb003Finished: data.filter((dt: any) => dt.checked === false).length === 0
				});
				setLoading(false);
				modalPerformCheckIn.current?.openModal();
			}catch(err){
				setLoading(false);
				standardErrorMessages(err, translate("Erro ao selecionar Milestones"), addToast);
			}
			return;
		}
		if(measurementForm.toString() === MeasurementForm.INICIATIVES || measurementForm.toString() === MeasurementForm.PROJECTS || measurementForm.toString() === MeasurementForm.INDICATORS){
			// CONTINUAR DESENVOLVENDO ESSES METODOS
			let method = "SelectCheckInIndicators";
			if(measurementForm.toString() === MeasurementForm.PROJECTS)
				method = "SelectCheckInProjects";
			if(measurementForm.toString() === MeasurementForm.INICIATIVES)	
				method = "SelectCheckInIniciatives";

			let percentToUpdate = typeof percent === "number" ? percent : stringToNumber(percent);

			try{
				setLoading(true);
				const {data} = await RCCServicesApi.getRequest({
					method: `KR/${method}`,
					urlParameters: {
						krId
					}
				});
				percentToUpdate = data;
				setPercentWhenTypeNotKR(percentToUpdate);
				setLoading(false);
				modalPerformCheckIn.current?.openModal();
			}catch(err){
				setLoading(false);
				standardErrorMessages(err, translate("Erro ao buscar valor do check in"), addToast);
			}

			setSelectedKr({
				okb001Id: krId,
				okb001Tipomedicao: measurementType,
				okb001Formamedicao: measurementForm,
				realizedValuesCheckIn: realizedValue,
				percent: percentToUpdate,
				okb001Valorinicial: initialValue,
				okb001Meta: goal,
				colorGraph
			});

			return;
		}
		setSelectedKr({
			okb001Id: krId,
			okb001Tipomedicao: measurementType,
			okb001Formamedicao: measurementForm,
			realizedValuesCheckIn: realizedValue,
			percent,
			okb001Valorinicial: initialValue,
			okb001Meta: goal,
			colorGraph
		});
		modalPerformCheckIn.current?.openModal();
	}, []);
	const submitPerformCheckIn = async (data: any) => {
		try{	
			clearErrorsPerformCheckIn();
			modalPerformCheckIn.current?.setLoadingButton();

			// Milestone
			if(selectedKr.okb001Tipomedicao.toString() === "4"){
				await checkInMilestoneSchema(translate).validate(data, {
					abortEarly: false
				});	
				const body = {
					krId: selectedKr.okb001Id,
					title: modalSubtitle,
					...data,
					oktbb003: data.oktbb003.filter((okb003: any) => okb003 !== null).map((okb003 : any) => ({
						...okb003,
						okb003Verificado: okb003.okb003Verificado ? "S" : "N"
					}))
				};
		
				await RCCServicesApi.postRequest({
					method: "KR/InsertCheckInMilestones",
					body 
				});
			}
			// Check In Normal
			else{
				await checkInSchema(translate).validate({...data, value: selectedKr.okb001Formamedicao.toString() !== MeasurementForm.KEY_RESULTS ? percentWhenTypeNotKR : data.value }, {
					abortEarly: false
				});	
				const body = {
					krId: selectedKr.okb001Id,
					title: modalSubtitle,
					...data
				};
				
				const initialValue = selectedKr.realizedValuesCheckIn + selectedKr.okb001Valorinicial;

				if(selectedKr.okb001Formamedicao.toString() !== MeasurementForm.KEY_RESULTS)
					body.value = percentWhenTypeNotKR - initialValue;
				else
					body.value = stringToNumber(data.value, currentLanguage) - initialValue;
	
				await RCCServicesApi.postRequest({
					method: "KR/InsertCheckIn",
					body 
				});
			}


			addToast({
				color: "green",
				description: translate("Check In adicionado com sucesso"),
				title: translate("Sucesso")
			});
			modalPerformCheckIn.current?.resetLoadingButton();
			modalPerformCheckIn.current?.closeModal();

			if(onCallbackAddOrChange.current !== undefined)
				onCallbackAddOrChange.current(selectedKr.okb001Id);

		}catch(err){
			modalPerformCheckIn.current?.resetLoadingButton();
			if(getValidationErrors(err, setErrorPerformCheckIn)){
				return;
			}
			standardErrorMessages(err, translate("Erro ao inserir Check In"), addToast);
		}
	};

	useImperativeHandle(ref, () => ({
		openModalPerformCheckIn
	}));

	return (
		<>
			{/* Modal CheckIn KR */}
			<Modal 
				ref={modalPerformCheckIn}
				header={{
					title: translate("Realizar Check In"),
					icon: {
						name: "checkSquare"
					},
					subtitle: modalSubtitle && `${translate("Resultado Chave")}: ${modalSubtitle}`
				}}
				footer= {{
					showSaveButton: !selectedKr.oktbb003Finished,
					buttonLoadingText: translate("Carregando"),
					saveButtonText: translate("Salvar"),
					cancelButtonText: translate("Cancelar")
				}}
				onSubmit={handleSubmitPerformCheckIn(submitPerformCheckIn)}
				styleInline={selectedKr.okb001Tipomedicao && selectedKr.okb001Tipomedicao.toString() !== "4"  ? styledModalCheckIn : styledModalCheckInMilestone}
			>
				{selectedKr.okb001Tipomedicao && selectedKr.okb001Tipomedicao.toString() !== "4" ? (
					<>
						<Title color="purple" size="md">
							{translate("Detalhes")}
						</Title>

						<DivCheckInModal>

							<RCCGrid
								container
								item
								sm={4}
								xs={12}
								direction="column"
							>
								<RCCGrid 
									container
									alignItems="center"
									justifyContent="center"
								>
									<Title color="grey" size="xs" bold> {translate("Tipo de Medição")}:</Title>
									{selectedKr.okb001Tipomedicao.toString() === MeasurementType.NUMERIC && (<span>{translate("Numérico")}</span>)}
									{selectedKr.okb001Tipomedicao.toString() === MeasurementType.PERCENTAGE && (<span>{translate("Percentual")}</span>)}
									{selectedKr.okb001Tipomedicao.toString() === MeasurementType.MONETARY_VALUE && (<span>{translate("Valor Monetário")}</span>)}
									{selectedKr.okb001Tipomedicao.toString() === MeasurementType.MILESTONE && (<span>{translate("Milestone")}</span>)}
								</RCCGrid>
								<RCCGrid
									container
									alignItems="center"
									justifyContent="center"
								>
									<Title color="grey" size="xs" bold> {translate("Forma de Medição")}:</Title>
									{selectedKr.okb001Formamedicao.toString() === MeasurementForm.KEY_RESULTS && (<span>{translate("Resultados Chave")}</span>)}
									{selectedKr.okb001Formamedicao.toString() === MeasurementForm.INICIATIVES && (<span>{translate("Iniciativas")}</span>)}
									{selectedKr.okb001Formamedicao.toString() === MeasurementForm.PROJECTS && (<span>{translate("Projetos")}</span>)}
									{selectedKr.okb001Formamedicao.toString() === MeasurementForm.INDICATORS && (<span>{translate("Indicadores")}</span>)}
								</RCCGrid>
							</RCCGrid>

							<RCCGrid
								container
								item
								sm={4}
								xs={12}
								direction="column"
							>
								{selectedKr.okb001Valorinicial ? (
									<RCCGrid 
										container
										alignItems="center"
										justifyContent="center"
									>
										<Title color="grey" size="xs" bold> {translate("Valor Inicial")}:</Title>
										<span>{formatNumber(selectedKr.okb001Valorinicial, currentLanguage)}</span>
									</RCCGrid>
								) : null}

								<RCCGrid 
									container
									alignItems="center"
									justifyContent="center"
								>
									<Title color="grey" size="xs" bold> {translate("Realizado")}:</Title>
									<span>{formatNumber(selectedKr.realizedValuesCheckIn + selectedKr.okb001Valorinicial, currentLanguage)}</span>
								</RCCGrid>

								{selectedKr.okb001Meta ? (
									<RCCGrid 
										container
										alignItems="center"
										justifyContent="center"
									>
										<Title color="grey" size="xs" bold> {translate("Meta")}:</Title>
										<span>{formatNumber(selectedKr.okb001Meta, currentLanguage)}</span>
									</RCCGrid>
								) : null}

							</RCCGrid>

							<RCCGrid
								container
								item
								sm={4}
								xs={12}
								justifyContent="center"
								alignItems="center"
							>
								<RCCProgress 
									layout="circle"
									value={Number(selectedKr.percent.toFixed(2))}
									color={selectedKr.colorGraph && selectedKr.colorGraph !== null && selectedKr.colorGraph !== "" ? `#${selectedKr.colorGraph}` : undefined}
									width="70px"
								/>
							</RCCGrid>
						</DivCheckInModal>

						<RCCGrid 
							container
							item
							justifyContent="center"
							spacing={2}
						>
							<RCCGrid
								container
								item
								xs={12}
								sm={4}
							>
								<DateTimePicker 
									type="datePicker"
									label={translate("Data")}
									name="date"
									defaultValue={new Date()}	
									control={controlPerformCheckIn}
									error={errorsPerformCheckIn.date}
									styleInline="margin: 5px 0;"
									required
								/>
							</RCCGrid>

							<RCCGrid
								container
								item
								alignItems="center"
								justifyContent="center"
								xs={12}
								sm={3}
							>
								{selectedKr.okb001Tipomedicao.toString() !== MeasurementType.PERCENTAGE ? (
									<InputNumber 
										label={translate("Valor")}
										name="value"
										defaultValue={0}	
										control={controlPerformCheckIn}
										error={errorsPerformCheckIn.value}
										required
										
									/>
								) : <>
									{selectedKr.okb001Formamedicao.toString() !== MeasurementForm.KEY_RESULTS &&
										<>
											<Title color="grey" size="xs" bold> {translate("Valor")}:</Title>
											<span style={{ fontSize: "0.85rem" }}>{formatNumber(percentWhenTypeNotKR, currentLanguage)}</span>
										</>
									}
								</>}
							</RCCGrid>

						</RCCGrid>

						{selectedKr.okb001Tipomedicao.toString() === MeasurementType.PERCENTAGE && 
						selectedKr.okb001Formamedicao.toString() === MeasurementForm.KEY_RESULTS && (
							<RCCGrid 
								container
								item
								justifyContent="center"
								spacing={2}
							>
								<RCCGrid
									container
									item
									xs={12}
									sm={7}
								>
									<RCCRangeSlider
										control={controlPerformCheckIn}
										name="value"
										color="purple"
										label={translate("Valor")}
										defaultValue={selectedKr.realizedValuesCheckIn + selectedKr.okb001Valorinicial}
										marks={[
											{
												value: 0,
												label: "0"
											},
											{
												value: selectedKr.okb001Valorinicial > selectedKr.okb001Meta ? selectedKr.okb001Valorinicial + selectedKr.okb001Meta : 100,
												label: selectedKr.okb001Valorinicial > selectedKr.okb001Meta ? (selectedKr.okb001Valorinicial + selectedKr.okb001Meta).toString() : "100"
											}
										]}
										min={1}
										max={selectedKr.okb001Valorinicial > selectedKr.okb001Meta ? selectedKr.okb001Valorinicial + selectedKr.okb001Meta : 100}
										valueLabelDisplay="on"
										valueLabelFormat={(value) => `${value}%`}
										styleInline=".MuiSlider-valueLabel > span > span{ color: var(--input-font-color); }"
									/>
								</RCCGrid>
							</RCCGrid>
						)}

						<RCCGrid 
							container
							item
							justifyContent="center"
							spacing={2}
						>
							<RCCGrid
								container
								item
								xs={12}
								sm={7}
							>
								<RCCTextArea 
									register={registerPerformCheckIn}
									name="comment"
									label={translate("Comentário")}
									maxLength={4000}
									error={errorsPerformCheckIn.comment}
									required										
								/>
							</RCCGrid>
						</RCCGrid>
					</>
				): (
					<>
						{!selectedKr.oktbb003Finished ? (
							<>
								<RCCGrid 
									container
									item
									justifyContent="center"
									spacing={2}
								>
									<RCCGrid
										container
										item
										xs={12}
										sm={4}
									>
										<DateTimePicker 
											type="datePicker"
											label={translate("Data")}
											name="date"
											defaultValue={new Date()}	
											control={controlPerformCheckIn}
											error={errorsPerformCheckIn.date}
											styleInline="margin: 5px 0;"
											required
										/>
									</RCCGrid>

									<RCCGrid
										container
										item
										xs={12}
										sm={3}
									>
										<>&nbsp;</>
									</RCCGrid>

								</RCCGrid>

								<RCCGrid 
									container
									item
									justifyContent="center"
									spacing={2}
								>
									<RCCGrid
										container
										item
										xs={12}
										sm={7}
									>
										<RCCTextArea 
											register={registerPerformCheckIn}
											name="comment"
											label={translate("Comentário")}
											maxLength={4000}
											error={errorsPerformCheckIn.comment}
											required										
										/>
									</RCCGrid>
								</RCCGrid>
							</>
						): (
							<RCCAlert 
								title={translate("Atenção")}
								description={translate("Você já concluiu todos os milestones")}
							/>
						)}

						{showTimeLine && (
							<TimelineVertical data={selectedKr.oktbb003} 
								nameOfCheckbox="okb003Verificado"
								nameOfID="okb003Id"
								nameOfObject="oktbb003"
								nameOfValue="okb003Percincr"
								register={registerPerformCheckIn}
							/>
						)}

					</>
				)}
			</Modal>
		
		</>
	);
};

export default forwardRef(CheckInModal);
